/**
 * Instalação guiada do rastreador — o processo inteiro, e ele só termina quando o aparelho fala.
 *
 * **O estado é DERIVADO, não clicado.** Nada de "próximo/anterior": cada passo pergunta ao dado se
 * já está feito, então voltar amanhã mostra exatamente onde parou. Não existe caixa «enviei o SMS»,
 * porque a prova de que o SMS funcionou não é alguém dizer que mandou — é o aparelho ter reportado.
 *
 * **O último passo não se marca: acontece.** Enquanto o primeiro sinal não chega, a tela fica
 * esperando e perguntando ao servidor a cada dez segundos. Uma instalação "concluída" sem posição
 * nenhuma seria uma mentira útil só para quem quer riscar item de lista — e inútil para quem
 * precisa saber se o carro está sendo rastreado.
 */
import { Check, RefreshCw, Server, Wifi, WifiOff } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  ComboBuscaEntidade,
  type ConfigBusca,
  type ItemBusca,
} from '@/components/crud/ComboBuscaEntidade';
import { ComandoSms, type Comando } from '@/components/crud/ComandoSms';
import { ComboReferencia } from '@/components/crud/ComboReferencia';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Chip, Spinner } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

type Comandos = { destino: string | null; servidor: string | null; comandos: Comando[] };
type Instalacao = {
  veiculo_id: string;
  placa: string | null;
  marca: string | null;
  modelo: string | null;
  desde: string;
  rastreamento_ativo: boolean;
} | null;
type Estado = { nunca_reportou: boolean; total_posicoes: number; em?: string };

/** De quanto em quanto tempo perguntar se o sinal chegou. Dez segundos é rápido o bastante para
 * quem está com o aparelho na mão e leve o bastante para deixar a aba aberta o dia inteiro. */
const INTERVALO_ESPERA_MS = 10_000;

const BUSCA_VEICULO: ConfigBusca = {
  endpoint: '/cadastro/veiculos',
  rotulo: (v) => String(v.placa || v.modelo || 'Veículo'),
  sub: (v) => [v.marca, v.modelo].filter(Boolean).map(String).join(' ') || null,
  placeholder: 'Buscar veículo por placa ou modelo…',
  camposRapido: [
    { chave: 'placa', rotulo: 'Placa', tipo: 'texto' },
    { chave: 'modelo', rotulo: 'Modelo', tipo: 'texto' },
  ],
};

function Passo({
  n,
  titulo,
  feito,
  resumo,
  children,
}: {
  n: number;
  titulo: string;
  feito: boolean;
  resumo?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex gap-3 border-t border-border py-4 first:border-t-0 first:pt-0">
      <div
        className={cn(
          'mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold',
          feito ? 'bg-success text-on-success' : 'border border-border text-text-tertiary',
        )}
      >
        {feito ? <Check className="size-3.5" /> : n}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-[15px] font-semibold text-text">{titulo}</span>
          {resumo}
        </div>
        {children}
      </div>
    </div>
  );
}

export function PainelInstalacaoGuiada({
  rastreadorId,
  chipId,
  traccarDeviceId,
  recarregar,
}: {
  rastreadorId: string;
  chipId: string | null;
  traccarDeviceId: number | null;
  recarregar: () => Promise<void>;
}) {
  const [cmds, setCmds] = useState<Comandos | null>(null);
  const [inst, setInst] = useState<Instalacao>(null);
  const [estado, setEstado] = useState<Estado | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [veiculo, setVeiculo] = useState<ItemBusca | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval>>(undefined);

  const carregar = useCallback(async () => {
    const [c, i, e] = await Promise.all([
      api.get<Comandos>(`/cadastro/rastreadores/${rastreadorId}/comandos`),
      api.get<Instalacao>(`/cadastro/rastreadores/${rastreadorId}/instalacao`),
      api.get<Estado>(`/cadastro/rastreadores/${rastreadorId}/estado`),
    ]);
    if (c.ok) setCmds(c.dados);
    if (i.ok) setInst(i.dados);
    if (e.ok) setEstado(e.dados);
    setCarregando(false);
  }, [rastreadorId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const conectou = !!estado && !estado.nunca_reportou;

  // Espera ativa pelo primeiro sinal — e SÓ por ele. Uma vez que o aparelho falou, não há mais o
  // que vigiar aqui, e continuar perguntando seria bater no servidor à toa o dia inteiro.
  useEffect(() => {
    if (conectou || carregando) return;
    timer.current = setInterval(() => void carregar(), INTERVALO_ESPERA_MS);
    return () => clearInterval(timer.current);
  }, [conectou, carregando, carregar]);

  async function vincularChip(id: string) {
    setSalvando(true);
    const r = await api.patch(`/cadastro/rastreadores/${rastreadorId}`, { chip_id: id || null });
    setSalvando(false);
    if (!r.ok) return void toast.error(r.erro);
    await recarregar();
    await carregar();
  }

  async function sincronizar() {
    setSincronizando(true);
    const r = await api.post(`/cadastro/rastreadores/${rastreadorId}/sincronizar`);
    setSincronizando(false);
    if (!r.ok) return void toast.error(r.erro);
    toast.success('Aparelho registrado no servidor de rastreamento.');
    await recarregar();
  }

  async function instalar() {
    if (!veiculo) return;
    setSalvando(true);
    const r = await api.post(`/cadastro/rastreadores/${rastreadorId}/instalar`, {
      veiculo_id: veiculo.id,
    });
    setSalvando(false);
    if (!r.ok) return void toast.error(r.erro);
    setVeiculo(null);
    await carregar();
  }

  async function alternarRastreamento(ativo: boolean) {
    setSalvando(true);
    const r = await api.patch(`/cadastro/rastreadores/${rastreadorId}/instalacao`, { ativo });
    setSalvando(false);
    if (!r.ok) return void toast.error(r.erro);
    await carregar();
  }

  if (carregando) {
    return (
      <Card>
        <CardContent className="py-8">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  const temChip = !!chipId && !!cmds?.destino;
  const noServidor = traccarDeviceId != null;
  const essenciais = (cmds?.comandos ?? []).filter((c) => c.essencial);
  const avancados = (cmds?.comandos ?? []).filter((c) => !c.essencial);
  const prontos = [temChip, noServidor, !!inst, conectou].filter(Boolean).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {conectou ? (
            <Check className="size-4 text-success" />
          ) : (
            <Spinner className="size-4 text-accent" />
          )}
          {conectou ? 'Rastreador em operação' : 'Pondo para funcionar'}
        </CardTitle>
        <p className="text-[13px] leading-[1.5] text-text-secondary">
          {conectou
            ? 'O aparelho está reportando. Daqui em diante é sozinho.'
            : `${prontos} de 4 prontos — a instalação só termina quando a primeira posição chegar.`}
        </p>
      </CardHeader>
      <CardContent className="flex flex-col">
        <Passo
          n={1}
          titulo="O chip que está no aparelho"
          feito={temChip}
          resumo={
            temChip ? <Chip className="border-success/40 text-success">{cmds?.destino}</Chip> : null
          }
        >
          <p className="tipo-corpo-pequeno text-text-tertiary">
            É para este número que vão os comandos — e é dele que sai o APN da operadora.
          </p>
          <div className="max-w-md">
            <ComboReferencia
              id="chip-do-rastreador"
              valor={chipId ?? ''}
              onChange={(id) => void vincularChip(id)}
              endpoint="/cadastro/chips"
              chaveRotulo="numero"
            />
          </div>
        </Passo>

        <Passo
          n={2}
          titulo="Registrado no servidor de rastreamento"
          feito={noServidor}
          resumo={
            noServidor ? (
              <Chip className="border-success/40 text-success">id {traccarDeviceId}</Chip>
            ) : (
              <Chip className="border-warning/40 text-warning">pendente</Chip>
            )
          }
        >
          <p className="tipo-corpo-pequeno text-text-tertiary">
            {noServidor
              ? 'O servidor conhece este aparelho e vai aceitar o que ele mandar.'
              : 'Feito sozinho no cadastro. Se ficou pendente, o servidor estava fora do ar — o aparelho conectaria e seria recusado em silêncio.'}
          </p>
          {!noServidor && (
            <Button
              size="sm"
              variant="secundario"
              className="self-start"
              disabled={sincronizando}
              onClick={() => void sincronizar()}
            >
              {sincronizando ? <Spinner /> : <Server />} Registrar agora
            </Button>
          )}
        </Passo>

        <Passo
          n={3}
          titulo="Em que veículo ele está"
          feito={!!inst}
          resumo={
            inst ? (
              <Chip className="border-success/40 text-success">{inst.placa ?? 'veículo'}</Chip>
            ) : null
          }
        >
          {inst ? (
            <div className="flex flex-wrap items-center gap-3">
              <span className="tipo-corpo-pequeno text-text-secondary">
                {[inst.marca, inst.modelo].filter(Boolean).join(' ')} · desde{' '}
                {new Date(inst.desde).toLocaleDateString('pt-BR')}
              </span>
              <Button
                size="sm"
                variant={inst.rastreamento_ativo ? 'fantasma' : 'secundario'}
                disabled={salvando}
                onClick={() => void alternarRastreamento(!inst.rastreamento_ativo)}
              >
                {inst.rastreamento_ativo ? <Wifi className="text-success" /> : <WifiOff />}
                {inst.rastreamento_ativo ? 'Rastreando — desligar' : 'Parado — ligar'}
              </Button>
            </div>
          ) : (
            <div className="flex max-w-md flex-col gap-2">
              <ComboBuscaEntidade config={BUSCA_VEICULO} onSelecionar={setVeiculo} />
              <Button
                size="sm"
                variant="secundario"
                className="self-start"
                disabled={!veiculo || salvando}
                onClick={() => void instalar()}
              >
                Instalar neste veículo
              </Button>
            </div>
          )}
        </Passo>

        <Passo
          n={4}
          titulo="Ensinar o aparelho, por SMS"
          feito={conectou}
          resumo={
            !temChip ? <Chip className="border-warning/40 text-warning">falta o chip</Chip> : null
          }
        >
          {!temChip ? (
            <p className="tipo-corpo-pequeno text-text-tertiary">
              Escolha o chip acima para os comandos saberem para onde ir.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="rounded-md border border-border bg-surface-2 p-3">
                <p className="tipo-corpo-pequeno text-text-secondary">
                  O rastreador sai de fábrica sem saber que este sistema existe. Quem conta a ele é
                  um SMS — e só o primeiro, porque depois disso ele passa a falar pela rede de dados
                  e o SMS sai de cena para sempre.
                </p>
                <ul className="tipo-corpo-pequeno mt-2 flex list-disc flex-col gap-1 pl-4 text-text-secondary">
                  <li>
                    Mande <strong>na ordem</strong>: sem rede (1º) ele não alcança o servidor (2º).
                  </li>
                  <li>
                    Espere o <span className="tipo-dado">SET OK</span> de cada um antes do próximo.
                    Sem resposta = o aparelho está sem sinal de celular; mandar o resto não adianta.
                  </li>
                  <li>
                    Deixe o rastreador <strong>a céu aberto</strong>. GPS não pega dentro de prédio,
                    e sem posição ele não tem o que reportar.
                  </li>
                </ul>
              </div>
              {essenciais.map((c, i) => (
                <ComandoSms key={c.chave} c={c} destino={cmds?.destino ?? null} ordem={i + 1} />
              ))}
            </div>
          )}
        </Passo>

        {/* O portão. Não se marca — acontece. */}
        <div
          className={cn(
            'mt-2 flex items-start gap-3 rounded-md border p-4',
            conectou ? 'border-success/40 bg-success/5' : 'border-warning/40 bg-warning/5',
          )}
        >
          {conectou ? (
            <Check className="mt-0.5 size-5 shrink-0 text-success" />
          ) : (
            <Spinner className="mt-0.5 size-5 shrink-0 text-warning" />
          )}
          <div className="flex flex-col gap-1">
            <span className="text-[15px] font-semibold text-text">
              {conectou ? 'Primeiro sinal recebido' : 'Aguardando o primeiro sinal'}
            </span>
            <p className="tipo-corpo-pequeno text-text-secondary">
              {conectou ? (
                <>
                  {estado?.total_posicoes.toLocaleString('pt-BR')} posições recebidas. A última em{' '}
                  {estado?.em ? new Date(estado.em).toLocaleString('pt-BR') : '—'}.
                </>
              ) : (
                <>
                  Esta tela está perguntando ao servidor a cada 10 segundos e vira verde sozinha
                  quando o aparelho falar. Costuma levar alguns minutos depois do último SMS, com o
                  aparelho a céu aberto — GPS não pega dentro de prédio.
                </>
              )}
            </p>
            {!conectou && (
              <Button
                size="sm"
                variant="fantasma"
                className="mt-1 self-start"
                onClick={() => void carregar()}
              >
                <RefreshCw /> Conferir agora
              </Button>
            )}
          </div>
        </div>

        {avancados.length > 0 && temChip && (
          <details className="mt-4 border-t border-border pt-4">
            <summary className="tipo-rotulo cursor-pointer select-none text-text-secondary hover:text-text">
              Comandos avançados ({avancados.length})
            </summary>
            <div className="mt-3 flex flex-col gap-3">
              {avancados.map((c) => (
                <ComandoSms key={c.chave} c={c} destino={cmds?.destino ?? null} />
              ))}
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
}
